/**
 * Helper utilities for cache debug UI.
 *
 * @module cache-debug/cache-debug-helpers
 */

import type { CacheDebugStep } from './useCacheDebugData';

// Selector formatting is shared with the playbook viewer — single source of
// truth in selector-format.ts. Re-exported here so existing cache-debug imports
// keep working.
export {
  getPrimarySelectorType,
  formatSelectorValue,
  selectorTypeBadgeColor,
} from '../../selector-format';

type ExecutionStrategy = {
  label: string;
  color: string;
  dotClass: string;
  description: string;
};

export function getExecutionStrategy(step: CacheDebugStep): ExecutionStrategy {
  if (step.cacheOutcome === 'hit') {
    if (step.cacheHitSource === 'recovery') {
      return {
        label: 'L2 Recovery',
        color: 'text-emerald-600 dark:text-emerald-400',
        dotClass: 'bg-emerald-500',
        description: 'Recovery agent repaired a failed cached action',
      };
    }
    if (step.cacheHitSource === 'wait_shortcut') {
      return {
        label: 'Wait Shortcut',
        color: 'text-blue-600 dark:text-blue-400',
        dotClass: 'bg-blue-500',
        description: 'Resolved via wait-only shortcut without agent',
      };
    }
    if (step.stepDrivenReplay) {
      return {
        label: 'Cached (step-driven)',
        color: 'text-blue-600 dark:text-blue-400',
        dotClass: 'bg-blue-500',
        description: 'Replayed via playbook-step-driven replay',
      };
    }
    return {
      label: 'Cached',
      color: 'text-blue-600 dark:text-blue-400',
      dotClass: 'bg-blue-500',
      description: 'Deterministic cache replay',
    };
  }

  if (step.cacheOutcome === 'fallback') {
    return {
      label: 'Agent (replay failed)',
      color: 'text-amber-600 dark:text-amber-400',
      dotClass: 'bg-amber-500',
      description: step.cacheOutcomeReason ?? 'Cache replay failed, fell back to agent',
    };
  }

  if (step.cacheOutcome === 'miss') {
    return {
      label: 'Agent',
      color: 'text-gray-500 dark:text-gray-400',
      dotClass: 'bg-gray-400',
      description: step.cacheOutcomeReason ?? 'No cache available',
    };
  }

  if (step.cacheOutcome === 'skipped') {
    return {
      label: 'Skipped',
      color: 'text-gray-400 dark:text-gray-500',
      dotClass: 'bg-gray-300 dark:bg-gray-600',
      description: step.cacheOutcomeReason ?? 'Cache lookup skipped',
    };
  }

  return {
    label: 'Unknown',
    color: 'text-gray-400 dark:text-gray-500',
    dotClass: 'bg-gray-300 dark:bg-gray-600',
    description: 'No cache outcome data',
  };
}

